'use strict'
import React, { useEffect, useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import { Link } from '@mui/material'
import { useModal, useModalExtra } from '../../../_components/Modal'
import { useModifyClassList } from '../../../common/utils/CSSUtils'
import { useConfirmationModal } from '../../../_components/Modal/useConfirmationModal'
import { useToast } from '../../../_components/Toast'
import Forbidden from '../../../_components/Forbidden'
import LoadingMessage from '../../../_components/LoadingMessage'
import CustomTooltip from '../../../_components/CustomTooltip'
import { SpinnerButton } from '../../common/CustomButton'
import { titleName, dataFixStatus } from '../../../_constants/Code'
import { errorLog } from '../../../common/logger/Logger'
import FileUploadArea from './components/FileUploadArea'
import FileCard from './components/FileCard'
import { useFileContentsModal } from './components/FileContentModal'
import { useFileClassification } from './components/FileClassification'
import { useCancelConfirmModal } from './components/CancelConfirmModal'
import {
  FileManagementProviderStore,
  useFileManagementDispatch,
  useFileManagementSelector,
  useFileManagementStore,
  shallowEqual
} from './extra/store'
import {
  generateFileKey,
  processFileStreaming,
  createFileStore,
  isFileDeleted,
  isCsvFile,
  baseLocalForage,
  normalizeExistingFileFromApi,
  buildFileObjectFromParsedData,
  buildUpdatedFileAfterChange,
  calculateNextFileStateAfterSheetChange,
  detectChangedFileKeys,
  prepareFileStoreForUpload,
  shallowEqualArrays,
  cloneExclusionConditions,
  applyPreviousSettings
} from './extra/helpers'
import { FILE_STATE } from './extra/types'
import {
  saveFileToIndexDb,
  deleteFileFromIndexDb,
  clearIndexDbAtScreenLoad,
  clearIndexDbForTask,
  registerFiles,
  saveFileClassifications,
  uploadPendingFilesToS3,
  loadInitialData,
  downloadAndProcessExistingFile,
  persistFileReferenceToIndexDb,
  getConversionValuesForTable,
  resetConversionValuesInFlight,
  fetchGenericTaskScreenForTask,
  fetchConversionValues,
  fetchPreviousFileSettings
} from './extra/services'
import {
  baseResetTargetCodes,
  needsStatusRollbackWaitOnRegister,
  snapshotStatusRollbackWatch,
  waitForGenericTaskStatusRollback
} from './extra/registerRefreshWait'
import {
  validateNewFileForUpload,
  validateParsedDataForUpload,
  validateFileChangeHeaders,
  validateFileChangeSheetName,
  getRegisteredHeaderBaselineForSheetChange,
  collectRegisterValidationErrors,
  collectConversionTableCoverageErrors,
  collectPrimaryKeyEmptyValueErrors,
  findExclusionValueValidationErrorsByFileKey
} from './extra/validation'
import {
  getUniqueValuesForExclusion,
  buildUnsetClassificationUpdates,
  filterExclusionValuesForNewFile
} from './extra/helpers'
import { PERIOD_KIND_LABELS, PERIOD_KIND_MONTHLY, ALLOWED_EXTENSIONS } from './extra/constants'
import '../../../assets/css/style.css'
import '../../../assets/css/common.css'

/** 要素を画面中央付近へスムーズスクロール（scrollIntoView 非対応時は window.scrollTo にフォールバック） */
const scrollElementIntoViewCenter = (elementId) => {
  const el = document.getElementById(elementId)
  if (!el) return
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  } catch {
    // fallback below
  }
  const rect = el.getBoundingClientRect()
  const targetY = rect.top + window.pageYOffset - window.innerHeight / 2 + rect.height / 2
  window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' })
}

/** FileManagementContentコンポーネント */
const FileManagementContent = (props) => {
  const { dataCreateTaskKey, userSub, userName, corporationNumber } = props
  const { _closeUpModal } = useModalExtra()
  const showToast = useToast()
  const openConfirmationModal = useConfirmationModal()
  const dispatch = useFileManagementDispatch()
  const store = useFileManagementStore()
  const stateRef = useRef(null)
  useEffect(() => {
    return store.subscribe(() => {
      stateRef.current = store.getState()
    })
  }, [store])
  const getLatestState = useCallback(() => stateRef.current || store.getState(), [store])

  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const [idConversionTables, setIdConversionTables] = React.useState([])
  // 変換表項目を取得中の「ファイル:変換表」の集合。複数ファイルが同時取得しうるため単一フラグにはしない
  const [loadingConversionKeys, setLoadingConversionKeys] = React.useState(() => new Set())
  const [isPostRegisterWaiting, setIsPostRegisterWaiting] = React.useState(false)
  // ファイル解析処理（新規選択・差し替え・シート変更）の実行中フラグ。大容量ファイルでは分単位かかるため処理中表示を出す
  const [isFileProcessing, setIsFileProcessing] = React.useState(false)
  const [exclusionValueErrorIndicesByFileKey, setExclusionValueErrorIndicesByFileKey] = React.useState({})

  const actions = useMemo(() => {
    return {
      reset: () => dispatch('RESET'),
      setPageState: (pageState) => dispatch('SET_PAGE_STATE', pageState),
      setTaskId: (taskId) => dispatch('SET_TASK_ID', taskId),
      setDataCreateTask: (dataCreateTask) => dispatch('SET_DATA_CREATE_TASK', dataCreateTask),
      setFileClassificationList: (list) => dispatch('SET_FILE_CLASSIFICATION_LIST', list),
      setSavedUploadState: (uploadState) => dispatch('SET_SAVED_UPLOAD_STATE', uploadState),
      addFile: (fileKey, fileState) => dispatch('ADD_FILE', { fileKey, fileState }),
      updateFile: (fileKey, updates) => dispatch('UPDATE_FILE', { fileKey, updates }),
      syncFileMeta: (fileKey, updates) => dispatch('SYNC_FILE_META', { fileKey, updates }),
      deleteFile: (fileKey) => dispatch('DELETE_FILE', { fileKey }),
      restoreFile: (fileKey) => dispatch('RESTORE_FILE', { fileKey }),
      removeFile: (fileKey) => dispatch('REMOVE_FILE', { fileKey }),
      initFileData: (fileKey, updates) => dispatch('INIT_FILE_DATA', { fileKey, updates }),
      toggleExpandedFile: (fileKey) => dispatch('TOGGLE_EXPANDED_FILE', { fileKey }),
      resetChangedFlags: () => dispatch('RESET_CHANGED_FLAGS'),
      setErrorMessages: (messages, clear = false) => dispatch('SET_ERROR_MESSAGES', { messages, clear }),
      setUsedMappingFileTypes: (types) => dispatch('SET_USED_MAPPING_FILE_TYPES', types)
    }
  }, [dispatch])

  const _processExistingFilesIntoState = useCallback(
    (existingFilesRes, dataCreateTaskRes, corporationNumber) => {
      if (!existingFilesRes.body || existingFilesRes.body.length === 0) {
        actions.setSavedUploadState({})
        actions.resetChangedFlags()
        return
      }

      const initialFileList = {}
      const taskKey = dataCreateTaskRes.body.taskKey

      // 1. まず全ファイルを同期的に state に追加して一覧を表示可能にする
      for (const fileData of existingFilesRes.body) {
        const file = normalizeExistingFileFromApi(fileData)
        if (!file?.fileKey) continue
        actions.addFile(file.fileKey, file)
        initialFileList[file.fileKey] = { ...file }

        const shouldDownload = file.fileState === FILE_STATE.UPLOADED || file.fileState === FILE_STATE.VALIDATION_ERROR
        // Note: FILE_STATE.PENDING_VIRUS_CHECK / VIRUS_CHECK_IN_PROGRESS はダウンロード対象外
        // （変換済みファイルが企業バケットに出力されるのはウイルスチェック完了後のため）

        if (shouldDownload) {
          // 2. ダウンロード処理を開始（await しない）
          downloadAndProcessExistingFile(file, taskKey, corporationNumber)
            .then(({ headers, isStoredLocally }) => {
              // 初期化時のデータ補完（isChangedを立てず、ベースラインも更新）
              actions.initFileData(file.fileKey, { headers, isStoredLocally })
            })
            .catch((error) => {
              // ダウンロード失敗は一時エラーの可能性があるため、ステータスは書き換えず未保持のまま扱う
              console.error(error?.stack ?? error)
              errorLog('E000001', userSub, 'FileManagement', 'downloadFileFromS3_background', error)
              actions.initFileData(file.fileKey, { isStoredLocally: false })
            })
        }
      }

      // 3. 同期的にベースラインを設定し、初期化による変更フラグをクリア
      actions.setSavedUploadState(initialFileList)
      actions.resetChangedFlags()
    },
    [actions, corporationNumber, userSub]
  )

  const pageState = useFileManagementSelector((s) => s.pageState)
  const dataCreateTask = useFileManagementSelector((s) => s.dataCreateTask)
  const isTaskFixed = dataCreateTask?.dataFixStatus === dataFixStatus.fix.value
  const fileClassificationList = useFileManagementSelector((s) => s.fileClassificationList)
  const errorMessages = useFileManagementSelector((s) => s.errorMessages)
  const fileList = useFileManagementSelector((s) => s.fileList)
  const usedMappingFileTypes = useFileManagementSelector((s) => s.usedMappingFileTypes, shallowEqual)
  const usedMappingFileTypeSet = useMemo(() => new Set(usedMappingFileTypes), [usedMappingFileTypes])

  // 未保存変更は fileList から導出（state に持たず useMemo で算出。core.mdc に準拠）
  const hasUnsavedChanges = useMemo(() => Object.values(fileList).some((f) => f.isChanged), [fileList])

  // 表示対象のファイルキー（pending-deletionを除外）: キー配列だけ購読して親の再レンダリングを抑える
  const displayFileKeys = useFileManagementSelector(
    (s) => Object.keys(s.fileList).filter((fileKey) => !isFileDeleted(s.fileList[fileKey]?.fileState)),
    shallowEqual
  )

  // 同一タスク内で他ファイルが使用中の FileClassification（fileKey ごと）。1 ファイル 1 種別のため select から除外する
  const fileClassificationsInUseByOthers = useMemo(() => {
    const m = {}
    for (const fk of displayFileKeys) {
      const others = displayFileKeys.filter((k) => k !== fk)
      m[fk] = new Set(others.map((k) => fileList[k]?.fileClassification).filter(Boolean))
    }
    return m
  }, [displayFileKeys, fileList])

  /** 変換表項目の取得中は登録できない（未取得のまま検証すると誤判定になるため） */
  const isConversionValuesLoading = loadingConversionKeys.size > 0

  const _setConversionLoading = useCallback((loadingKey, isLoading) => {
    setLoadingConversionKeys((prev) => {
      if (isLoading === prev.has(loadingKey)) return prev
      const next = new Set(prev)
      if (isLoading) next.add(loadingKey)
      else next.delete(loadingKey)
      return next
    })
  }, [])

  /**
   * 変換表項目をS3から取得する。
   *
   * 取得中は登録ボタンを非活性にする。同一ファイルで変換表を続けて切り替えた場合に 先に完了した取得が後続の取得中状態を消さないよう、ファイルと変換表の組で状態を持つ。
   * 取得失敗時も含め終了時には必ず取得中から外す（登録ボタンが戻らなくなるのを防ぐ）。 未取得のまま登録された場合も、登録時に再取得されるため検証は成立する。
   */
  const loadConversionValuesForFile = useCallback(
    async (fileKey, conversionTableKey) => {
      const loadingKey = `${fileKey}:${conversionTableKey}`
      _setConversionLoading(loadingKey, true)
      try {
        await getConversionValuesForTable(corporationNumber, conversionTableKey)
      } catch (err) {
        console.error('getConversionValuesForTable', err)
      } finally {
        _setConversionLoading(loadingKey, false)
      }
    },
    [corporationNumber, _setConversionLoading]
  )

  /** ファイルメタ（種別・キー項目・詳細設定）の更新。store 更新後に IndexedDB の reference も同期し、ファイル差し替えなしで変更できるようにする */
  const handleUpdateFile = useCallback(
    (fileKey, updates) => {
      const current = getLatestState().fileList[fileKey]
      if (current?.fileState === FILE_STATE.VIRUS_CHECK_IN_PROGRESS) return

      actions.updateFile(fileKey, updates)
      if (updates.idConversionTable !== undefined) {
        // 「変換しない」（空）の場合は取得不要。取得中の分は各取得の finally で解除される
        const nextTableKey = String(updates.idConversionTable || '').trim()
        if (nextTableKey !== '') {
          loadConversionValuesForFile(fileKey, nextTableKey)
        }
      }
      if (updates.exclusionConditions !== undefined) {
        setExclusionValueErrorIndicesByFileKey((prev) => {
          if (!prev[fileKey]) return prev
          const next = { ...prev }
          delete next[fileKey]
          return next
        })
      }
      const nextFile = getLatestState().fileList[fileKey]
      if (nextFile) {
        persistFileReferenceToIndexDb(fileKey, dataCreateTaskKey, nextFile).catch((err) => {
          console.error('persistFileReferenceToIndexDb', err)
        })
      }
    },
    [actions, dataCreateTaskKey, getLatestState, loadConversionValuesForFile]
  )

  /**
   * ファイル種別変更処理（仕様 9.9: ファイル種別選択時の設定自動反映）。 種別の選択を即時反映した後、IF02104 で同じファイル種別を最後に使用した過去タスクの設定を取得し、
   * 同名列照合（applyPreviousSettings）の結果で当該ファイルを更新する。 取得失敗時はエラーメッセージを表示せず、種別の選択のみ反映する（設定は手動で行える）。
   */
  const handleChangeFileClassification = useCallback(
    async (fileKey, newFileClassification) => {
      // 「未選択」に戻した場合は、種別に紐づく設定（キー項目・データ種別・ベースファイル・詳細設定）もクリアする
      if (!newFileClassification) {
        handleUpdateFile(fileKey, buildUnsetClassificationUpdates())
        return
      }
      handleUpdateFile(fileKey, { fileClassification: newFileClassification })

      try {
        const res = await fetchPreviousFileSettings(corporationNumber, dataCreateTaskKey, newFileClassification)
        if (!res?.found || !res.settings) return

        const state = getLatestState()
        const currentFile = state.fileList[fileKey]
        // 取得中に種別が変わった/ファイルが消えた場合は反映しない
        if (!currentFile || currentFile.fileClassification !== newFileClassification) return

        const isMonthlyTask = state.dataCreateTask?.periodKind === PERIOD_KIND_MONTHLY
        handleUpdateFile(fileKey, applyPreviousSettings(currentFile, res.settings, isMonthlyTask))
      } catch (error) {
        // 仕様 9.9: APIエラー時は自動設定を行わず、ファイル種別の選択のみ反映する（エラーメッセージは表示しない）
        console.error(error?.stack ?? error)
        errorLog('E000001', userSub, 'FileManagement', 'handleChangeFileClassification', error)
      }
    },
    [corporationNumber, dataCreateTaskKey, getLatestState, handleUpdateFile, userSub]
  )

  /** ローカルストアからのメタ同期（headers 救済）。ウイルスチェック中は store を変更しない */
  const handleSyncFileMeta = useCallback(
    (fileKey, updates) => {
      const current = getLatestState().fileList[fileKey]
      if (current?.fileState === FILE_STATE.VIRUS_CHECK_IN_PROGRESS) return
      actions.syncFileMeta(fileKey, updates)
    },
    [actions, getLatestState]
  )

  /** シート変更処理 */
  const handleChangeSheet = useCallback(
    async (fileKey, sheetName) => {
      const state = getLatestState()
      const file = state.fileList[fileKey]
      if (!file || file.fileType !== 'excel') return
      if (file.fileState === FILE_STATE.VIRUS_CHECK_IN_PROGRESS) return
      if (file.sheetName === sheetName) return

      const previousSheetName = file.sheetName

      setIsFileProcessing(true)
      try {
        // 先に選択状態だけ反映（処理は非同期で時間がかかる可能性があるため）
        actions.updateFile(fileKey, { sheetName })

        // ファイルストアを取得
        const fileStore = createFileStore(fileKey, dataCreateTaskKey)

        // シート変更は元のExcelファイルが必要なため、メタ領域に保存したファイルを参照する
        // フォールバック: ファイルストアにも保存しておき、メタ領域がクリアされてもシート変更できるようにする
        let sourceFile = await baseLocalForage.getItem(`source_file_${fileKey}`)
        if (!sourceFile) {
          sourceFile = await fileStore.getItem('source_file')
        }
        if (!sourceFile) {
          showToast('シート変更に必要なファイルデータが見つかりません。Excelファイルを再選択してください。')
          actions.updateFile(fileKey, { sheetName: previousSheetName })
          return
        }

        // 新しいシートを読み込み、ヘッダー/データをIndexedDBへ再保存
        const parsed = await processFileStreaming(sourceFile, fileStore, () => {}, sheetName)

        const nextHeaders = Array.isArray(parsed?.headers) ? parsed.headers : []
        const nextSheetName = parsed?.sheetName || sheetName
        const nextSheetList = Array.isArray(parsed?.sheetList) ? parsed.sheetList : file.sheetList || []

        // 登録済みファイルのみヘッダー整合性を検証（未登録の新規アップロードは任意のシートへ切り替え可能）
        const referenceHeaders = getRegisteredHeaderBaselineForSheetChange(
          fileKey,
          file,
          getLatestState().savedUploadState
        )
        if (referenceHeaders !== null && !shallowEqualArrays(nextHeaders, referenceHeaders)) {
          actions.setErrorMessages(
            [
              `選択したシート「${nextSheetName}」のヘッダーが登録済みファイルのヘッダーと一致していません。同じ列構成のシートを選択してください。`
            ],
            true
          )
          actions.updateFile(fileKey, { sheetName: previousSheetName })
          return
        }

        const nextState = calculateNextFileStateAfterSheetChange(file, nextHeaders, nextSheetName, nextSheetList)

        // processFileStreaming は fileStore.clear() を行うため、参照を再保存して整合性を保つ
        // また、元ファイルも保存しておき、再度シート変更できるようにする
        await fileStore.setItem('source_file', sourceFile)
        await fileStore.setItem('reference', { ...file, ...nextState })

        actions.updateFile(fileKey, nextState)
      } catch (error) {
        console.error(error?.stack ?? error)
        errorLog('E000001', userSub, 'FileManagement', 'handleChangeSheet', error)
        showToast('シートの変更に失敗しました。')
        actions.updateFile(fileKey, { sheetName: previousSheetName })
      } finally {
        setIsFileProcessing(false)
      }
    },
    [actions, showToast, getLatestState, userSub, dataCreateTaskKey]
  )

  /** ファイル種別保存処理（仕様: 現状のページ状態を一括送信。エラー時 body.message 配列は結合して表示） */
  const handleSaveFileTypes = useCallback(
    async (updatedFileClassificationList, payload) => {
      try {
        if (Array.isArray(payload) && payload.length > 0) {
          await saveFileClassifications(corporationNumber, payload)
        }
        // 保存後は削除済み（isPendingDelete）を除外したリストで store を更新する
        const listForStore = {
          defaultTypes: updatedFileClassificationList.defaultTypes || [],
          userTypes: (updatedFileClassificationList.userTypes || []).filter((ft) => !ft.isPendingDelete)
        }
        actions.setFileClassificationList(listForStore)
        showToast('ファイル種別を保存しました。')
      } catch (error) {
        console.error(error?.stack ?? error)
        errorLog('E000001', userSub, 'FileManagement', 'handleSaveFileTypes', error)
        const bodyMessage = error?.response?.data?.message ?? error?.message ?? ''
        const messages = Array.isArray(bodyMessage)
          ? bodyMessage.join('\n')
          : typeof bodyMessage === 'string'
            ? bodyMessage
            : 'ファイル種別の保存に失敗しました。'
        actions.setErrorMessages(messages ? [messages] : ['ファイル種別の保存に失敗しました。'])
      }
    },
    [actions, corporationNumber, showToast, userSub]
  )

  // モーダルフック（コンポーネントのトップレベルで呼び出す）
  const openFileContentsModal = useFileContentsModal(handleChangeSheet, dataCreateTaskKey)
  const openFileClassificationModal = useFileClassification(
    fileClassificationList,
    handleSaveFileTypes,
    corporationNumber
  )

  // 初期化（マウント時のみ実行。dataCreateTaskKey / corporationNumber はルート固定のため依存配列は空でよい）
  useEffect(() => {
    actions.reset()
    document.title = 'ファイル管理' + titleName.common
    window.scrollTo(0, 0)
    init(dataCreateTaskKey, corporationNumber)
  }, [])

  // 未保存変更時の離脱防止（beforeunload）。ALLOWED 外のため理由を明記: UX 上ブラウザ離脱前に警告する必要がある。
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  // ページ遷移時のIndexedDBクリア
  // 標準の初期化パターン外で実行される理由:
  // 1. ページ離脱時（アンマウント時）に、そのタスクで使用した一時データを確実にクリアするため
  // 2. 他の画面へ遷移した際に IndexedDB の容量を圧迫しないようにするため
  useEffect(() => {
    return () => {
      const p = clearIndexDbForTask(dataCreateTaskKey)
      if (p && typeof p.catch === 'function') {
        p.catch(console.error)
      }
      // 変換表項目は画面内に保持しているため、離脱時に破棄する
      resetConversionValuesInFlight()
    }
  }, [dataCreateTaskKey])

  // バリデーション／サーバエラー発生時に画面下のエラーセクションへ自動スクロール。
  // ALLOWED 外のため理由を明記: errorMessages は条件付き描画でマウントされるため、
  // 描画コミット後（=useEffect 実行時点）にスクロール対象が DOM に存在することが保証される。
  // event handler 側で rAF を使う旧実装ではコミット前に getElementById が null を返す競合があった (#2998 差し戻し原因)。
  // scrollIntoView は一部の overflow 親や埋め込みコンテキストで no-op になるため、
  // 失敗時は getBoundingClientRect で絶対位置を算出して window.scrollTo にフォールバックする。
  useEffect(() => {
    if (errorMessages.length === 0) return
    scrollElementIntoViewCenter('file-management-errors')
  }, [errorMessages])

  // 除外値未選択エラー発生時、該当の値選択エリアへ自動スクロール（詳細設定展開後の DOM コミットを待つ）
  useEffect(() => {
    const fileKeysWithErrors = Object.entries(exclusionValueErrorIndicesByFileKey).filter(
      ([, indices]) => indices.length > 0
    )
    if (fileKeysWithErrors.length === 0) return

    const firstFileKey =
      displayFileKeys.find((key) => exclusionValueErrorIndicesByFileKey[key]?.length > 0) ?? fileKeysWithErrors[0][0]
    const firstIndex = exclusionValueErrorIndicesByFileKey[firstFileKey][0]
    scrollElementIntoViewCenter(`file-management-exclusion-value-error-${firstFileKey}-${firstIndex}`)
  }, [exclusionValueErrorIndicesByFileKey, displayFileKeys])

  /**
   * 既登録ファイルの変換表項目を先読みする。
   *
   * 既登録ファイルは変換表の選択イベントが発生しないため、ここで取得しておく。 同じ変換表を複数ファイルが使う場合でもS3取得は1回のみ（ getConversionValuesForTable が変換表単位で重複を防ぐ）。
   */
  const _prefetchConversionValues = async (existingFiles) => {
    await Promise.all(
      (existingFiles ?? [])
        .filter((file) => file?.fileKey && String(file?.idConversionTable || '').trim() !== '')
        .map((file) => loadConversionValuesForFile(file.fileKey, String(file.idConversionTable).trim()))
    )
  }

  /** 初期化処理 */
  const init = async (taskKey, corpNum) => {
    try {
      actions.reset()
      actions.setPageState('loading')
      await clearIndexDbAtScreenLoad(taskKey)
      const [dataCreateTaskRes, fileClassificationListRes, existingFilesRes, idConversionTablesRes] =
        await loadInitialData(taskKey, corpNum)
      actions.setDataCreateTask(dataCreateTaskRes.body)
      actions.setFileClassificationList(fileClassificationListRes.body)
      setIdConversionTables(idConversionTablesRes.body)
      actions.setUsedMappingFileTypes(existingFilesRes.usedMappingFileTypes ?? [])

      // 既存ファイルの反映（ダウンロードはバックグラウンドで実行されるため待機しない）
      _processExistingFilesIntoState(existingFilesRes, dataCreateTaskRes, corpNum)

      // 既登録ファイルの変換表項目を先読みする（画面表示は待たせない）。
      // 取得完了までは登録ボタンを非活性にし、未取得のまま登録された場合も登録時に再取得される。
      // _processExistingFilesIntoState は待機しないため、state ではなく取得結果から対象を求める。
      _prefetchConversionValues(existingFilesRes.body).catch(console.error)

      actions.setPageState('idle')
    } catch (error) {
      console.error(error?.stack ?? error)
      errorLog('E000001', userSub, 'FileManagement', 'init', error)
      actions.setErrorMessages(['初期データの取得に失敗しました。'])
      actions.setPageState('error')
    }
  }

  /** 新規アップロード時の事前バリデーション（1ファイル） */
  /** 選択されたファイル1件を処理する */
  const processSelectedFile = async (file, errorHandler) => {
    if (!validateNewFileForUpload(file, getLatestState().fileList, errorHandler)) return

    const fileKey = generateFileKey()
    const { parsedData, isExcel } = await prepareFileStoreForUpload(fileKey, dataCreateTaskKey, file)

    if (!validateParsedDataForUpload(parsedData, file, errorHandler)) return

    const fileObject = buildFileObjectFromParsedData(parsedData, file, fileKey, userName)
    await saveFileToIndexDb(fileKey, dataCreateTaskKey, fileObject, parsedData)
    if (isExcel) {
      const fileStore = createFileStore(fileKey, dataCreateTaskKey)
      await baseLocalForage.setItem(`source_file_${fileKey}`, file)
      await fileStore.setItem('source_file', file)
    }
    actions.addFile(fileKey, fileObject)
  }

  /** ファイルアップロード処理 */
  const handleFilesSelected = async (files) => {
    const errors = []
    const errorHandler = (msg) => errors.push(msg)

    setIsFileProcessing(true)
    try {
      for (const file of files) {
        try {
          await processSelectedFile(file, errorHandler)
        } catch (fileError) {
          console.error(fileError?.stack ?? fileError)
          errorLog('E000001', userSub, 'FileManagement', 'handleFilesSelected_each', fileError)
          errors.push({ fileName: file.name, before: '', after: ': 処理中にエラーが発生しました' })
        }
      }
    } catch (error) {
      console.error(error?.stack ?? error)
      errorLog('E000001', userSub, 'FileManagement', 'handleFilesSelected', error)
      errors.push('ファイルのアップロード処理全体でエラーが発生しました。')
    } finally {
      setIsFileProcessing(false)
    }

    if (errors.length > 0) {
      actions.setErrorMessages(errors)
    }
  }

  /** ファイル差し替え共通処理（パース・IndexedDB保存・store更新） */
  const _applyFileUpdate = async (fileKey, newFile, parsedData) => {
    const fileAfterChange = buildUpdatedFileAfterChange(getLatestState().fileList[fileKey], newFile, parsedData)
    const fileStore = createFileStore(fileKey, dataCreateTaskKey)
    // 除外条件の選択値を変更後ファイルの内容に合わせる（変更前のファイルにしかない値は落とす）
    const exclusionConditions = await filterExclusionValuesForNewFile(fileAfterChange.exclusionConditions, (column) =>
      getUniqueValuesForExclusion(fileKey, column, fileStore)
    )
    const updatedFile = { ...fileAfterChange, exclusionConditions }
    await fileStore.setItem('reference', updatedFile)

    const allFileKeys = (await baseLocalForage.getItem('all_file_keys')) || {}
    const taskFileKeys = allFileKeys[dataCreateTaskKey] || []
    if (!taskFileKeys.includes(fileKey)) {
      taskFileKeys.push(fileKey)
      allFileKeys[dataCreateTaskKey] = taskFileKeys
      await baseLocalForage.setItem('all_file_keys', allFileKeys)
    }

    actions.updateFile(fileKey, updatedFile)
  }

  /** 未登録ファイルの差し替え処理（ヘッダー比較なし・そのまま上書き） */
  const _processFileUpdateUnregistered = async (fileKey, file, newFile) => {
    const errorHandler = (msg) => {
      actions.setErrorMessages([msg], true)
    }
    if (!validateNewFileForUpload(newFile, getLatestState().fileList, errorHandler)) return

    const isNewFileCsv = isCsvFile(newFile)
    const sheetNameForPrepare = isNewFileCsv || file.sheetName === '0' ? '' : file.sheetName || ''
    if (!(await validateFileChangeSheetName(file.sheetName, newFile, newFile.name, errorHandler))) return

    const { parsedData } = await prepareFileStoreForUpload(fileKey, dataCreateTaskKey, newFile, sheetNameForPrepare)

    if (!validateParsedDataForUpload(parsedData, newFile, errorHandler)) return

    await _applyFileUpdate(fileKey, newFile, parsedData)
  }

  /** 登録済みファイルの差し替え処理（savedUploadState または file.headers を基準に列一致を検証） */
  const _processFileUpdateRegistered = async (fileKey, file, newFile) => {
    const errorHandler = (msg) => {
      actions.setErrorMessages([msg], true)
    }
    if (!validateNewFileForUpload(newFile, getLatestState().fileList, errorHandler)) return

    const isNewFileCsv = isCsvFile(newFile)
    const sheetNameForPrepare = isNewFileCsv || file.sheetName === '0' ? '' : file.sheetName || ''
    if (!(await validateFileChangeSheetName(file.sheetName, newFile, newFile.name, errorHandler))) return

    const { parsedData } = await prepareFileStoreForUpload(fileKey, dataCreateTaskKey, newFile, sheetNameForPrepare)

    if (!validateParsedDataForUpload(parsedData, newFile, errorHandler)) return

    const savedUploadState = getLatestState().savedUploadState
    const savedRow = savedUploadState?.[fileKey]
    const savedHeaders = savedRow?.headers
    const referenceHeaders =
      Array.isArray(savedHeaders) && savedHeaders.length > 0 ? savedHeaders : (file.headers ?? [])
    if (!validateFileChangeHeaders(referenceHeaders, parsedData.headers, newFile.name, errorHandler)) return

    await _applyFileUpdate(fileKey, newFile, parsedData)
  }

  /** ファイル選択用の隠しinputを作成してクリックする */
  const _selectFileWithHiddenInput = useCallback((onSelect) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')
    input.style.display = 'none'

    input.onchange = async (e) => {
      const file = e.target.files[0]
      if (file) {
        await onSelect(file)
      }
      document.body.removeChild(input)
    }

    document.body.appendChild(input)
    input.click()
  }, [])

  /** ファイル変更処理 */
  const handleChangeFile = useCallback(
    async (fileKey) => {
      const state = getLatestState()
      const file = state.fileList[fileKey]
      if (!file) return
      if (file.fileState === FILE_STATE.VIRUS_CHECK_IN_PROGRESS) return

      _selectFileWithHiddenInput(async (newFile) => {
        setIsFileProcessing(true)
        try {
          const st = getLatestState()
          const latestFile = st.fileList[fileKey]
          if (!latestFile) return
          if (latestFile.fileState === FILE_STATE.VIRUS_CHECK_IN_PROGRESS) return
          const hasSavedBaselineForReplace = Boolean(st.savedUploadState?.[fileKey])
          const isServerRegisteredBaseline =
            latestFile.fileState === FILE_STATE.UPLOADED || latestFile.fileState === FILE_STATE.VALIDATION_ERROR
          // 列整合: savedUploadState がある場合、またはサーバー登録済み（uploaded / validation-error）は
          // file.headers を基準に必ず検証。それ以外のみ未登録扱いで上書き
          if (hasSavedBaselineForReplace || isServerRegisteredBaseline) {
            await _processFileUpdateRegistered(fileKey, latestFile, newFile)
          } else {
            await _processFileUpdateUnregistered(fileKey, latestFile, newFile)
          }
        } catch (error) {
          console.error(error?.stack ?? error)
          errorLog('E000001', userSub, 'FileManagement', 'handleChangeFile', error)
          actions.setErrorMessages(['ファイルの変更に失敗しました。'])
        } finally {
          setIsFileProcessing(false)
        }
      })
    },
    [actions, getLatestState, userSub, _selectFileWithHiddenInput]
  )

  /** ファイル削除処理 */
  const handleDeleteFile = useCallback(
    async (fileKey) => {
      const state = getLatestState()
      const file = state.fileList[fileKey]
      if (!file) return

      const isRegisteredFile =
        file.fileState === FILE_STATE.UPLOADED ||
        file.fileState === FILE_STATE.VIRUS_CHECK_ERROR ||
        file.fileState === FILE_STATE.VALIDATION_ERROR

      // マッピング設定で使用中のファイルか（IF02101 used_mapping_file_types とファイル種別で判定）
      const usedMappingFileTypeSet = new Set(state.usedMappingFileTypes || [])
      const isUsedInMapping = isRegisteredFile && usedMappingFileTypeSet.has(file.fileClassification)
      // ベースファイルか（削除すると全データ分類の結合キーが失われ、再検証が必要になる）
      const isBaseFile = isRegisteredFile && file.isBaseFile === true

      const fileNameNode = (
        <CustomTooltip title={file.fileName}>
          {(ref) => (
            <span ref={ref} className="truncate max-w-[350px] inline-block align-bottom">
              {file.fileName}
            </span>
          )}
        </CustomTooltip>
      )
      let description
      if (isBaseFile) {
        description = (
          <span className="break-all">
            「{fileNameNode}
            」は他のファイルの基準となるベースファイルです。削除すると、このベースに紐づくデータ分類の検証結果が無効になり、再検証（必要に応じてマッピングの再設定）が必要になります。削除しますか？
          </span>
        )
      } else if (isUsedInMapping) {
        description = (
          <span className="break-all">
            「{fileNameNode}
            」はマッピング設定で使用されています。削除すると、このファイルを使用しているマッピング設定は解除され、対象のデータ分類は再設定・再検証が必要になります。削除しますか？
          </span>
        )
      } else if (isRegisteredFile) {
        description = (
          <span className="inline-flex items-center flex-wrap">
            「{fileNameNode}」は既に登録されています。削除しますか？
          </span>
        )
      } else {
        description = <span className="inline-flex items-center flex-wrap">「{fileNameNode}」を削除しますか？</span>
      }

      await openConfirmationModal({
        title: '削除の確認',
        description,
        confirmName: '削除する',
        onClick: async () => {
          try {
            if (isRegisteredFile) {
              actions.deleteFile(fileKey)
              showToast('ファイルを削除予定に設定しました。')
            } else {
              await deleteFileFromIndexDb(fileKey, dataCreateTaskKey)
              await baseLocalForage.removeItem(`source_file_${fileKey}`)
              actions.removeFile(fileKey)
              showToast('ファイルを削除しました。')
            }
          } catch (error) {
            console.error(error?.stack ?? error)
            errorLog('E000001', userSub, 'FileManagement', 'handleDeleteFile', error)
            actions.setErrorMessages(['ファイルの削除に失敗しました。'])
          }
        }
      })
    },
    [actions, dataCreateTaskKey, openConfirmationModal, showToast, store, userSub]
  )

  /** ファイル内容表示 */
  const handleShowFileContent = useCallback(
    async (fileKey) => {
      const state = getLatestState()
      const file = state.fileList[fileKey]
      if (!file) return

      await openFileContentsModal(fileKey, file)
    },
    [openFileContentsModal, getLatestState]
  )

  /** 除外条件のユニーク値取得 */
  const handleGetUniqueValues = useCallback(
    async (fileKey, columnName, sheetName) => {
      const fileStore = createFileStore(fileKey, dataCreateTaskKey)
      return await getUniqueValuesForExclusion(fileKey, columnName, fileStore)
    },
    [dataCreateTaskKey]
  )

  /** ファイル種別管理モーダル */
  const handleOpenFileTypeManager = useCallback(() => {
    openFileClassificationModal()
  }, [openFileClassificationModal])

  /** 登録実行（モーダル確定後の処理） */
  const _uploadChangedFiles = useCallback(
    async (changedFiles) => {
      try {
        await uploadPendingFilesToS3(changedFiles, dataCreateTaskKey)
      } catch (error) {
        console.error(error?.stack ?? error)
        errorLog('E000001', userSub, 'FileManagement', 'uploadFileToS3', error)
        const fileName = error.fileName ?? ''
        actions.setErrorMessages([{ fileName, before: 'ファイル「', after: '」のアップロードに失敗しました。' }])
        throw error
      }
    },
    [actions, dataCreateTaskKey, userSub]
  )

  const _registerChangedFiles = useCallback(
    async (changedFileKeys, savedUploadState) => {
      const latestFileList = getLatestState().fileList
      const latestChangedFiles = changedFileKeys
        .map((k) => {
          const current = latestFileList[k]
          if (current) return current
          const saved = savedUploadState?.[k]
          if (saved) return { ...saved, fileState: FILE_STATE.PENDING_DELETION }
          return null
        })
        .filter(Boolean)

      await registerFiles(dataCreateTaskKey, corporationNumber, latestChangedFiles, savedUploadState)

      // 仕様 9.6: 送信したファイルの hashColumns / exclusionConditions を登録済みに反映
      for (const file of latestChangedFiles) {
        if (file && !isFileDeleted(file.fileState) && file.fileKey) {
          actions.initFileData(file.fileKey, {
            registeredHashColumns: Array.isArray(file.hashColumns) ? [...file.hashColumns] : [],
            registeredExclusionConditions: cloneExclusionConditions(file.exclusionConditions)
          })
        }
      }
    },
    [actions, corporationNumber, dataCreateTaskKey, getLatestState]
  )

  const executeRegister = useCallback(async () => {
    let shouldWaitStatusRollback = false
    /** @type {Record<string, string>} */
    let statusRollbackWatchSnapshot = {}
    try {
      const state = getLatestState()
      const { savedUploadState, fileList } = state
      const changedFileKeys = detectChangedFileKeys(savedUploadState, fileList)
      // removeFile で一覧から消えたファイルは fileList にないため、savedUploadState から PENDING_DELETION として復元する
      const changedFiles = changedFileKeys
        .map((k) => {
          const current = fileList[k]
          if (current) return current
          const saved = savedUploadState?.[k]
          if (saved) return { ...saved, fileState: FILE_STATE.PENDING_DELETION }
          return null
        })
        .filter(Boolean)

      shouldWaitStatusRollback = needsStatusRollbackWaitOnRegister(changedFiles, savedUploadState)
      if (shouldWaitStatusRollback) {
        const screenBeforeRegister = await fetchGenericTaskScreenForTask(corporationNumber, dataCreateTaskKey)
        // バックエンドが巻き戻す分類（従業員IDキー分類）のみ監視する。
        // 対象外の分類は巻き戻らないため、監視するとタイムアウトする（バグ#3233）
        statusRollbackWatchSnapshot = snapshotStatusRollbackWatch(
          screenBeforeRegister.statusByDataKind,
          baseResetTargetCodes(screenBeforeRegister.genericDataKinds)
        )
      }

      // 更新情報のセット
      for (const file of changedFiles) {
        if (file.fileState !== FILE_STATE.PENDING_DELETION) {
          await actions.updateFile(file.fileKey, {
            updateUserName: userName,
            updatedAt: new Date().toISOString()
          })
        }
      }

      // S3アップロード
      await _uploadChangedFiles(changedFiles)

      // 最終的な登録処理
      await _registerChangedFiles(changedFileKeys, savedUploadState)

      actions.setSavedUploadState({ ...getLatestState().fileList })
      actions.resetChangedFlags()
      await clearIndexDbForTask(dataCreateTaskKey)

      if (shouldWaitStatusRollback) {
        setIsPostRegisterWaiting(true)
        await waitForGenericTaskStatusRollback({
          watchSnapshot: statusRollbackWatchSnapshot,
          fetchScreen: () => fetchGenericTaskScreenForTask(corporationNumber, dataCreateTaskKey)
        })
      }

      // トーストは巻き戻し完了後・遷移直前に表示する（レポートデータ更新画面に戻ったタイミングで見えるようにする）
      showToast('ファイルを登録しました。')
      navigate(`/data/analysisData/${dataCreateTaskKey}`)
    } catch (error) {
      console.error(error?.stack ?? error)
      if (!errorMessages.length) {
        errorLog('E000001', userSub, 'FileManagement', 'executeRegister', error)
        if (error instanceof Error && error.message === 'STATUS_ROLLBACK_WAIT_TIMEOUT') {
          actions.setErrorMessages([
            '設定の反映に時間がかかっています。しばらく待ってからレポートデータ更新画面で状態を確認してください。'
          ])
        } else {
          actions.setErrorMessages(['ファイルの登録に失敗しました。'])
        }
      }
    } finally {
      setIsPostRegisterWaiting(false)
    }
  }, [
    actions,
    corporationNumber,
    dataCreateTaskKey,
    errorMessages.length,
    navigate,
    showToast,
    getLatestState,
    userName,
    userSub,
    _registerChangedFiles,
    _uploadChangedFiles
  ])

  /** 登録前バリデーション（変更なし・入力エラー・キー項目空データ・変換表カバレッジ） */
  const validateRegisterChanges = useCallback(async () => {
    const state = getLatestState()
    const { savedUploadState, fileList } = state
    const changedFileKeys = detectChangedFileKeys(savedUploadState, fileList)
    const changedFiles = changedFileKeys
      .map((k) => {
        const current = fileList[k]
        if (current) return current
        const saved = savedUploadState?.[k]
        if (saved) return { ...saved, fileState: FILE_STATE.PENDING_DELETION }
        return null
      })
      .filter(Boolean)

    if (changedFiles.length === 0) {
      showToast('変更されたファイルがありません。')
      return false
    }

    const validationErrors = collectRegisterValidationErrors(changedFiles, fileList, savedUploadState)
    if (validationErrors.length > 0) {
      setExclusionValueErrorIndicesByFileKey({})
      actions.setErrorMessages(validationErrors, true)
      return false
    }

    const primaryKeyEmptyErrors = await collectPrimaryKeyEmptyValueErrors(changedFiles, dataCreateTaskKey)
    if (primaryKeyEmptyErrors.length > 0) {
      setExclusionValueErrorIndicesByFileKey({})
      actions.setErrorMessages(primaryKeyEmptyErrors, true)
      return false
    }

    const exclusionValueErrorsByFileKey = findExclusionValueValidationErrorsByFileKey(changedFiles)
    if (Object.keys(exclusionValueErrorsByFileKey).length > 0) {
      actions.setErrorMessages([], true)
      setExclusionValueErrorIndicesByFileKey(exclusionValueErrorsByFileKey)
      const { expandedFiles } = state
      for (const fileKey of Object.keys(exclusionValueErrorsByFileKey)) {
        if (!expandedFiles.has(fileKey)) {
          actions.toggleExpandedFile(fileKey)
        }
      }
      return false
    }
    setExclusionValueErrorIndicesByFileKey({})

    // 取得済みなら再取得しない（未取得ならS3から取得）。先読みが失敗していても検証が成立する
    const coverageErrors = await collectConversionTableCoverageErrors(
      changedFiles,
      corporationNumber,
      dataCreateTaskKey,
      getConversionValuesForTable,
      savedUploadState,
      idConversionTables
    )
    if (coverageErrors.length > 0) {
      actions.setErrorMessages(coverageErrors, true)
      return false
    }

    return true
  }, [getLatestState, actions, showToast, corporationNumber, dataCreateTaskKey, idConversionTables])

  /** 登録処理（確認モーダル表示前にバリデーションを実行） */
  const handleRegister = useCallback(async () => {
    if (!(await validateRegisterChanges())) return

    await openConfirmationModal({
      title: '登録の確認',
      description: '登録してレポートデータ更新画面に戻りますか？',
      confirmName: '登録する',
      isSpinnerDisabled: false,
      onClick: async () => {
        await executeRegister()
      }
    })
  }, [openConfirmationModal, executeRegister, validateRegisterChanges])

  /** キャンセル確認の「保存して戻る」: 確認モーダルなしで登録を直接実行（完了まで未保存モーダルを表示） */
  const handleSaveAndReturn = useCallback(async () => {
    if (!(await validateRegisterChanges())) return
    await executeRegister()
  }, [validateRegisterChanges, executeRegister])

  const openCancelConfirmModal = useCancelConfirmModal({
    onDiscard: async () => {
      await clearIndexDbForTask(dataCreateTaskKey)
      navigate(`/data/analysisData/${dataCreateTaskKey}`)
    },
    onSave: handleSaveAndReturn
  })

  const handleCancel = useCallback(async () => {
    if (hasUnsavedChanges) {
      openCancelConfirmModal()
      return
    }

    // 未保存変更がない場合は直接戻る
    await clearIndexDbForTask(dataCreateTaskKey)
    navigate(`/data/analysisData/${dataCreateTaskKey}`)
  }, [dataCreateTaskKey, hasUnsavedChanges, navigate, openCancelConfirmModal])

  if (pageState === 'loading') {
    return <LoadingMessage message="読み込み中..." minHeightClassName="min-h-[240px]" />
  }

  if (pageState === 'error') {
    return (
      <div className="text-center py-10">
        {errorMessages.map((msg, idx) => (
          <div key={idx} className="red--text">
            {msg}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="page-container">
      {isPostRegisterWaiting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          role="status"
          aria-live="polite"
        >
          <div className="rounded-lg bg-white px-8 py-6 shadow-lg">
            <LoadingMessage message="設定を反映しています..." minHeightClassName="min-h-0" />
          </div>
        </div>
      )}
      {isFileProcessing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          role="status"
          aria-live="polite"
        >
          <div className="rounded-lg bg-white px-8 py-6 shadow-lg">
            <LoadingMessage message="ファイルを処理しています..." minHeightClassName="min-h-0" />
          </div>
        </div>
      )}
      <section className="page-content">
        <div className="centered-container">
          {/* 上階層に戻る */}
          <div className="bg main">
            <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
              <Link
                color="inherit"
                variant="button"
                underline="none"
                component={RouterLink}
                to={`/data/analysisData/${dataCreateTaskKey}`}
              >
                <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                  <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                  <span className="text-h5 font-bold">レポートデータ更新に戻る</span>
                </span>
              </Link>
            </div>
          </div>

          {/* コンテンツヘッダ */}
          <header className="page-header flex justify-center py-1 pt-8">
            <h1 className="page-header__name text-h1 grey--text py-8">ファイル管理</h1>
          </header>

          {/* 更新対象データ（レポートデータ更新画面と同様） */}
          {dataCreateTask && (
            <div className="sub-title-area flex justify-center items-center py-4">
              <h3 className="text-h4 font-bold m-0 leading-none">更新対象データ：</h3>
              <table className="base-table ml-5 m-0 w-auto table-fixed align-middle">
                <tbody className="text-h5 text-center">
                  <tr>
                    <td className="blue1 white--text align-middle w-[150px]">種類</td>
                    <td className="border-blue1 align-middle w-[150px]">
                      {PERIOD_KIND_LABELS[dataCreateTask.periodKind] ?? '年次'}
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className="base-table ml-5 m-0 w-auto table-fixed align-middle">
                <tbody className="text-h5 text-center">
                  <tr>
                    <td className="blue1 white--text align-middle w-[150px]">期間</td>
                    <td className="border-blue1 align-middle w-[150px]">{dataCreateTask.periodValue}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* コンテンツボディ */}
          <div className="page-body">
            <div className="centered-container">
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl mt-8 pt-10 pb-16 px-16 border-blue1 w-full max-w-none">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  ファイル管理
                </h2>

                <div className="container w-full max-w-none">
                  {isTaskFixed && (
                    <div className="box-xy-bordered border-blue1 bg-blue-50 mb-6 p-4 rounded-lg" role="alert">
                      <p className="text-body blue1--text text-center m-0 font-bold">
                        データ確定済のため、ファイルのアップロードや設定の変更はできません。
                      </p>
                    </div>
                  )}
                  <div className="border-t border-lightgray blue1 lighten-3"></div>

                  <div className="content-area">
                    {/* ファイルアップロードエリア */}
                    <div className="mt-5">
                      <h2 className="pb-3 mb-5 font-semibold text-gray-800 text-lg border-b-2 border-[#E0E5F2]">
                        1. ファイルをアップロード
                      </h2>
                      <p className="text-body text-gray-600 !text-left -mt-3 mb-4">
                        CSVファイルまたはExcelファイル（.xlsx、.xls）をアップロードできます。ファイルをアップロード後、各ファイルに
                        <strong>ファイル種別</strong>と<strong>キー項目</strong>を設定してください
                      </p>
                      <FileUploadArea onFilesSelected={handleFilesSelected} disabled={isTaskFixed} />
                    </div>

                    {/* ファイル設定セクション */}
                    <div className="mt-10">
                      <div className="flex items-center justify-between pb-3 mb-5 font-semibold text-gray-800 text-lg border-b-2 border-[#E0E5F2]">
                        <span>2. ファイルの設定</span>
                        <button
                          type="button"
                          className="btn m-0 !bg-[#E0E5F2] !text-[#3150A5] hover:!bg-[#d0d5e2] border-0 rounded-md px-6 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={handleOpenFileTypeManager}
                          disabled={isTaskFixed}
                        >
                          📋 ファイル種別を管理
                        </button>
                      </div>
                      <p className="text-caption-2 grey--text mb-4">
                        アップロードしたファイルに対して、ファイル種別やキー項目などの設定を行います。
                      </p>

                      {/* ファイルリスト */}
                      {displayFileKeys.length === 0 ? (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-caption-2 text-yellow-800">
                          ⚠️ アップロードされたファイルがありません。ファイルをアップロードしてください。
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {displayFileKeys.map((fileKey) => (
                            <FileCardContainer
                              key={fileKey}
                              fileKey={fileKey}
                              taskKey={dataCreateTaskKey}
                              fileClassificationList={fileClassificationList}
                              fileClassificationsInUseByOthers={fileClassificationsInUseByOthers[fileKey]}
                              idConversionTables={idConversionTables}
                              onToggleExpand={actions.toggleExpandedFile}
                              onUpdateFile={handleUpdateFile}
                              onSyncFileMeta={handleSyncFileMeta}
                              onDeleteFile={handleDeleteFile}
                              onChangeFile={handleChangeFile}
                              onShowFileContent={handleShowFileContent}
                              onChangeSheet={handleChangeSheet}
                              onGetUniqueValues={handleGetUniqueValues}
                              onChangeFileClassification={handleChangeFileClassification}
                              isTaskFixed={isTaskFixed}
                              usedMappingFileTypeSet={usedMappingFileTypeSet}
                              exclusionValueErrorIndices={exclusionValueErrorIndicesByFileKey[fileKey] ?? []}
                            />
                          ))}
                        </div>
                      )}

                      {/* エラーメッセージ */}
                      {errorMessages.length > 0 && (
                        <div id="file-management-errors" className="mt-4">
                          {errorMessages.map((msg, idx) => {
                            if (typeof msg === 'string') {
                              return (
                                <div key={idx} className="red--text text-caption-2">
                                  {msg}
                                </div>
                              )
                            }
                            return (
                              <div key={idx} className="red--text text-caption-2 flex items-center min-w-0">
                                <span>{msg.before}</span>
                                <CustomTooltip title={msg.fileName}>
                                  {(ref) => (
                                    <span ref={ref} className="truncate max-w-[300px] inline-block align-bottom">
                                      {msg.fileName}
                                    </span>
                                  )}
                                </CustomTooltip>
                                <span>{msg.after}</span>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* 操作ボタン */}
                    <div className="content-footer mt-16 mb-8">
                      <div className="border-t border-blue1" />
                      <div className="footer_button_area mt-8 flex gap-4 justify-end">
                        <button type="button" className="footer-btn button_back" onClick={handleCancel}>
                          キャンセル
                        </button>
                        <SpinnerButton
                          name="登録"
                          className="btn footer-btn button_base disabled:!cursor-not-allowed disabled:!opacity-60"
                          onClick={handleRegister}
                          disabled={isTaskFixed || isConversionValuesLoading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

/**
 * FileCard の描画単位を fileKey に落とし、親の再レンダリングを抑える
 *
 * @param {any} props
 */
const FileCardContainer = (props) => {
  const { fileKey, exclusionValueErrorIndices = [] } = props
  const file = useFileManagementSelector((s) => s.fileList[fileKey])
  const isExpanded = useFileManagementSelector((s) => s.expandedFiles.has(fileKey))
  const savedFileClassification = useFileManagementSelector(
    (s) => s.savedUploadState?.[fileKey]?.fileClassification ?? ''
  )
  const savedPrimaryKeyColumn = useFileManagementSelector((s) => s.savedUploadState?.[fileKey]?.primaryKeyColumn ?? '')
  const savedDataCategory = useFileManagementSelector((s) => s.savedUploadState?.[fileKey]?.dataCategory ?? '')
  const periodKind = useFileManagementSelector((s) => s.dataCreateTask?.periodKind ?? '')

  if (!file) return null

  const usedMappingFileTypeSet = props.usedMappingFileTypeSet
  const isDetailSettingsLockedByMapping =
    Boolean(file.fileClassification) && usedMappingFileTypeSet?.has(file.fileClassification)

  return (
    <FileCard
      {...props}
      file={file}
      isExpanded={isExpanded}
      savedFileClassification={savedFileClassification}
      savedPrimaryKeyColumn={savedPrimaryKeyColumn}
      savedDataCategory={savedDataCategory}
      periodKind={periodKind}
      isDetailSettingsLockedByMapping={isDetailSettingsLockedByMapping}
      exclusionValueErrorIndices={exclusionValueErrorIndices}
    />
  )
}

/** FileManagement（Main）: 権限チェック・StoreProvider・Content に props を渡す */
const FileManagement = () => {
  const state = useSelector((s) => s)
  const { dataCreateTaskKey } = useParams()
  const showModal = useModal()
  const showToast = useToast()

  // 正規導線「ファイルを管理」リンクの活性条件（利用可否 + 更新またはアップロードのみ権限）と同一の権限のみ許可（URL直接遷移対策）
  const authConf = state.authority.Upload_AuthInfo?.Auth_Conf
  if (!state.authority.Upload_AuthInfo?.Availability || !(authConf?.Regist || authConf?.Upload)) {
    return <Forbidden />
  }

  if (!state.company.corporationNumber || !dataCreateTaskKey) {
    return <LoadingMessage message="読み込み中..." minHeightClassName="min-h-[240px]" />
  }

  return (
    <FileManagementProviderStore>
      <FileManagementContent
        dataCreateTaskKey={dataCreateTaskKey}
        userSub={state.user.sub}
        userName={state.user.userName}
        corporationNumber={state.company.corporationNumber}
      />
    </FileManagementProviderStore>
  )
}

export default FileManagement
